import React, { useState } from 'react';
import { HelpCircle, Send, MessageCircle, Mail, Clock } from 'lucide-react';
import { ModulePlaceholder } from '@/components/modules/common/ModulePlaceholder';
import { useAuthContext } from '@/hooks/useAuthContext';

export const SupportView: React.FC = () => {
  const { user } = useAuthContext();
  const [messages, setMessages] = useState([
    {
      id: '1',
      sender: 'Conversora Partner Support',
      text: 'Hello! How can the partner team assist with your referral campaigns, marketing materials, or payouts today?',
      time: 'Just now',
      isMe: false,
    },
  ]);
  const [inputText, setInputText] = useState('');

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputText.trim()) return;

    const newMsg = {
      id: String(Date.now()),
      sender: user?.fullName || 'Partner',
      text: inputText.trim(),
      time: 'Just now',
      isMe: true,
    };

    setMessages((prev) => [...prev, newMsg]);
    setInputText('');

    setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        {
          id: String(Date.now() + 1),
          sender: 'Partner Support',
          text: 'Thank you for reaching out! A partner concierge will review your message and reply promptly. You can also reach us directly via WhatsApp for urgent queries.',
          time: 'Just now',
          isMe: false,
        },
      ]);
    }, 1000);
  };

  return (
    <ModulePlaceholder
      title="Partner Support & Help Center"
      subtitle="Connect directly with the Conversora partner relations team for guidance and assistance"
      icon={HelpCircle}
      badge="Dedicated Concierge"
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <a
            href="https://wa.me/8801700000000"
            target="_blank"
            rel="noreferrer"
            className="p-4 bg-[#0E1015] hover:bg-zinc-900/60 border border-zinc-800 rounded-2xl flex items-center gap-3.5 transition group"
          >
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 group-hover:scale-105 transition-transform">
              <MessageCircle className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">WhatsApp Support</p>
              <p className="text-xs text-zinc-400">Direct chat with partner manager</p>
            </div>
          </a>

          <a
            href="mailto:partners@conversora.io"
            className="p-4 bg-[#0E1015] hover:bg-zinc-900/60 border border-zinc-800 rounded-2xl flex items-center gap-3.5 transition group"
          >
            <div className="w-10 h-10 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400 group-hover:scale-105 transition-transform">
              <Mail className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Email Desk</p>
              <p className="text-xs text-zinc-400">partners@conversora.io</p>
            </div>
          </a>

          <div className="p-4 bg-[#0E1015] border border-zinc-800 rounded-2xl flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400">
              <Clock className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Support Hours</p>
              <p className="text-xs text-zinc-400">Sat – Thu, 10:00 AM – 8:00 PM BST</p>
            </div>
          </div>
        </div>

        {/* Messaging Box */}
        <div className="bg-[#0E1015] border border-zinc-800 rounded-2xl p-6">
          <h2 className="text-sm font-semibold text-white mb-4">Direct Message Desk</h2>
          <div className="h-64 overflow-y-auto space-y-3 p-4 bg-zinc-950 rounded-xl border border-zinc-800/80 mb-4">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex flex-col ${msg.isMe ? 'items-end' : 'items-start'}`}
              >
                <div
                  className={`max-w-md p-3 rounded-xl text-xs leading-relaxed ${
                    msg.isMe
                      ? 'bg-indigo-600 text-white rounded-br-xs'
                      : 'bg-zinc-900 text-zinc-200 border border-zinc-800 rounded-bl-xs'
                  }`}
                >
                  <p className="font-semibold text-[10px] text-zinc-400 mb-0.5">{msg.sender}</p>
                  <p>{msg.text}</p>
                </div>
                <span className="text-[10px] text-zinc-500 mt-1 px-1">{msg.time}</span>
              </div>
            ))}
          </div>

          <form onSubmit={handleSend} className="flex gap-2">
            <input
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Type your message for partner support..."
              className="flex-1 bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2.5 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-indigo-500"
            />
            <button
              type="submit"
              className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer shadow-xs"
            >
              <Send className="w-3.5 h-3.5" />
              <span>Send</span>
            </button>
          </form>
        </div>
      </div>
    </ModulePlaceholder>
  );
};

export default SupportView;
